import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "~/atoms/empty";
import { includesSearch } from "~/helpers/string";
import { useBulkSelection, type BulkSelection } from "~/hooks/useBulkSelection";
import { type ApplyTemplateRow } from "~/hooks/useApplyTemplateRows";
import { useWorkflowTemplateFilters } from "~/hooks/useWorkflowTemplateFilters";
import { BulkSelectionBar } from "~/molecules/BulkSelectionBar";
import { FilterPopover } from "~/molecules/FilterPopover";
import SearchBar from "~/molecules/SearchBar";
import SimpleDataTable from "~/molecules/SimpleDataTable";
import { TableSkeleton } from "~/molecules/TableSkeleton";
import { SelectActiveFilterChips } from "~/organisms/filters/SelectActiveFilterChips";
import { buildApplyTemplateColumns } from "./applyTemplateColumns";

interface Props {
  templateId: number | null;
  rows: ApplyTemplateRow[];
  tagMap: Map<number, string>;
  isLoading: boolean;
  actions?: (
    selection: BulkSelection<number>,
    selectedRows: ApplyTemplateRow[],
  ) => React.ReactNode;
}

export function ApplyTemplateTable({
  templateId,
  rows,
  tagMap,
  isLoading,
  actions,
}: Props) {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");

  const {
    filters,
    activeFilters,
    handleSelectFilter,
    handleClearFilters,
    filterItems,
  } = useWorkflowTemplateFilters({ rows, tagMap });

  const visibleRows = filterItems(rows).filter(
    (row) => !search || includesSearch(row.title, search),
  );

  const selection = useBulkSelection(visibleRows.map((row) => row.id));

  const selectedRows = rows.filter((row) => selection.selectedIds.has(row.id));

  const columns = buildApplyTemplateColumns({
    templateId,
    tagMap,
    selection,
    t,
  });

  return (
    <Card className="gap-2">
      <CardHeader>
        <CardTitle>
          {t(($) => $.workflowTemplates.documentsTable.title)}
        </CardTitle>
        <CardDescription>
          {t(($) => $.workflowTemplates.documentsTable.description)}
        </CardDescription>
        <div className="flex flex-wrap items-center gap-2">
          <FilterPopover
            filters={filters}
            activeFiltersCount={activeFilters.length}
            onClearFilters={handleClearFilters}
            onSelectFilter={handleSelectFilter}
          />
          <SearchBar
            placeholder={t(
              ($) => $.workflowTemplates.documentsTable.searchPlaceholder,
            )}
            value={search}
            onSearch={setSearch}
            onDebouncedSearch={setSearch}
            onClearSearch={() => setSearch("")}
          />
          <SelectActiveFilterChips
            activeFilters={activeFilters}
            onSelectFilter={handleSelectFilter}
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <TableSkeleton
            rows={5}
            columns={7}
            wrapperClassName="max-h-110 rounded-md border"
          />
        ) : visibleRows.length === 0 ? (
          <Empty className="h-fit border-0 py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileText />
              </EmptyMedia>
              <EmptyDescription>
                {t(($) => $.workflowTemplates.documentsTable.noDocuments)}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="flex flex-col gap-1">
            <BulkSelectionBar
              count={selectedRows.length}
              onUnselectAll={selection.clearSelection}
            />
            <SimpleDataTable
              columns={columns}
              data={visibleRows}
              onClickRow={(row) => selection.toggleRow(row.id)}
              wrapperClassName="max-h-110 rounded-md border"
              headerClassName="bg-muted"
            />
          </div>
        )}
      </CardContent>
      {actions && <CardFooter>{actions(selection, selectedRows)}</CardFooter>}
    </Card>
  );
}
